'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';

export type HouseTheme =
  | 'blue'
  | 'green'
  | 'halloween'
  | 'red'
  | 'usa'
  | 'white'
  | 'yellow';

type ThemeDef = {
  key: HouseTheme;
  label: string;
  src: string; // relative to /public
};

const THEMES: ThemeDef[] = [
  { key: 'blue',      label: 'Blue',      src: '/lit/houses/blue-house.png' },
  { key: 'green',     label: 'Green',     src: '/lit/houses/green-house.png' },
  { key: 'halloween', label: 'Halloween', src: '/lit/houses/halloween-house.png' },
  { key: 'red',       label: 'Red',       src: '/lit/houses/red-house.png' },
  { key: 'usa',       label: 'USA',       src: '/lit/houses/usa-house.png' },
  { key: 'white',     label: 'White',     src: '/lit/houses/white-house.png' },
  { key: 'yellow',    label: 'Yellow',    src: '/lit/houses/yellow-house.png' },
];

export type HouseLightsGalleryProps = {
  initial?: HouseTheme;
  autoplayMs?: number; // e.g. 3000 to cycle, omit to disable
  className?: string;
};

export default function HouseLightsGallery({
  initial = 'blue',
  autoplayMs,
  className,
}: HouseLightsGalleryProps) {
  const [active, setActive] = useState<HouseTheme>(initial);
  const order = useMemo(() => THEMES.map(t => t.key), []);
  const idx = order.indexOf(active);
  const timerRef = useRef<number | null>(null);

  // Autoplay
  useEffect(() => {
    if (!autoplayMs) return;
    stop();
    timerRef.current = window.setInterval(() => {
      setActive(prev => {
        const i = order.indexOf(prev);
        return order[(i + 1) % order.length];
      });
    }, Math.max(autoplayMs, 800));
    return stop;
  }, [autoplayMs, order]);

  function stop() {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
  }

  // Keyboard left/right
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        setActive(order[(idx + 1) % order.length]);
      } else if (e.key === 'ArrowLeft') {
        setActive(order[(idx - 1 + order.length) % order.length]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, order]);

  const activeTheme = THEMES.find(t => t.key === active)!;

  return (
    <section className={className}>
      {/* Image */}
      <div
        role="group"
        aria-label={`House lights preview: ${activeTheme.label}`}
        className="relative mx-auto w-full max-w-4xl overflow-hidden rounded-2xl shadow aspect-[4/3] sm:aspect-[16/9]"
      >
        <Image
          src={activeTheme.src}
          alt={`${activeTheme.label} house lights`}
          fill
          sizes="(max-width: 768px) 100vw, 900px"
          priority
          style={{ objectFit: 'cover' }}
          onLoad={stop} // small guard to avoid first-frame swap during load
        />
        {/* Prev/Next */}
        <button
          aria-label="Previous"
          onClick={() => setActive(order[(idx - 1 + order.length) % order.length])}
          className="absolute left-1.5 top-1/2 -translate-y-1/2 rounded-full bg-black/40 px-2.5 py-1.5 text-xs text-white sm:left-2 sm:px-3 sm:py-2 sm:text-sm"
        >
          ←
        </button>
        <button
          aria-label="Next"
          onClick={() => setActive(order[(idx + 1) % order.length])}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-black/40 px-2.5 py-1.5 text-xs text-white sm:right-2 sm:px-3 sm:py-2 sm:text-sm"
        >
          →
        </button>
        <div className="absolute bottom-1.5 right-1.5 rounded bg-black/50 px-2 py-1 text-[10px] text-white sm:bottom-2 sm:right-2 sm:text-xs">
          {activeTheme.label}
        </div>
      </div>

      {/* Theme selector */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {THEMES.map(t => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            aria-pressed={active === t.key}
            className={`rounded-full border px-3 py-1 text-sm transition
              ${
                active === t.key
                  ? 'border-transparent bg-cyan-400 text-black' // Active state (matches site accent)
                  : 'border-gray-700 bg-gray-900/50 text-gray-300 hover:bg-gray-800' // Inactive state (dark theme)
              }`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </section>
  );
}
